/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  transformEncoder,
  type AccountMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";

export const RESOLVE_DID_DOCUMENT_DISCRIMINATOR = new Uint8Array([
  168, 180, 218, 211, 184, 19, 102, 12,
]);

export function getResolveDidDocumentDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    RESOLVE_DID_DOCUMENT_DISCRIMINATOR,
  );
}

export type ResolveDidDocumentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountDidDocument extends string | AccountMeta<string> = string,
  TAccountDidController extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountDidDocument extends string
        ? ReadonlyAccount<TAccountDidDocument>
        : TAccountDidDocument,
      TAccountDidController extends string
        ? ReadonlyAccount<TAccountDidController>
        : TAccountDidController,
      ...TRemainingAccounts,
    ]
  >;

export type ResolveDidDocumentInstructionData = {
  discriminator: ReadonlyUint8Array;
};

export type ResolveDidDocumentInstructionDataArgs = {};

export function getResolveDidDocumentInstructionDataEncoder(): FixedSizeEncoder<ResolveDidDocumentInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([["discriminator", fixEncoderSize(getBytesEncoder(), 8)]]),
    (value) => ({
      ...value,
      discriminator: RESOLVE_DID_DOCUMENT_DISCRIMINATOR,
    }),
  );
}

export function getResolveDidDocumentInstructionDataDecoder(): FixedSizeDecoder<ResolveDidDocumentInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
  ]);
}

export function getResolveDidDocumentInstructionDataCodec(): FixedSizeCodec<
  ResolveDidDocumentInstructionDataArgs,
  ResolveDidDocumentInstructionData
> {
  return combineCodec(
    getResolveDidDocumentInstructionDataEncoder(),
    getResolveDidDocumentInstructionDataDecoder(),
  );
}

export type ResolveDidDocumentAsyncInput<
  TAccountDidDocument extends string = string,
  TAccountDidController extends string = string,
> = {
  /** DID document account */
  didDocument?: Address<TAccountDidDocument>;
  /** Controller public key (used for PDA derivation) */
  didController: Address<TAccountDidController>;
};

export async function getResolveDidDocumentInstructionAsync<
  TAccountDidDocument extends string,
  TAccountDidController extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: ResolveDidDocumentAsyncInput<
    TAccountDidDocument,
    TAccountDidController
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  ResolveDidDocumentInstruction<
    TProgramAddress,
    TAccountDidDocument,
    TAccountDidController
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    didDocument: { value: input.didDocument ?? null, isWritable: false },
    didController: { value: input.didController ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Resolve default values.
  if (!accounts.didDocument.value) {
    accounts.didDocument.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            100, 105, 100, 95, 100, 111, 99, 117, 109, 101, 110, 116,
          ]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.didController.value)),
      ],
    });
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.didDocument),
      getAccountMeta(accounts.didController),
    ],
    data: getResolveDidDocumentInstructionDataEncoder().encode({}),
    programAddress,
  } as ResolveDidDocumentInstruction<
    TProgramAddress,
    TAccountDidDocument,
    TAccountDidController
  >);
}

export type ResolveDidDocumentInput<
  TAccountDidDocument extends string = string,
  TAccountDidController extends string = string,
> = {
  /** DID document account */
  didDocument: Address<TAccountDidDocument>;
  /** Controller public key (used for PDA derivation) */
  didController: Address<TAccountDidController>;
};

export function getResolveDidDocumentInstruction<
  TAccountDidDocument extends string,
  TAccountDidController extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: ResolveDidDocumentInput<TAccountDidDocument, TAccountDidController>,
  config?: { programAddress?: TProgramAddress },
): ResolveDidDocumentInstruction<
  TProgramAddress,
  TAccountDidDocument,
  TAccountDidController
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    didDocument: { value: input.didDocument ?? null, isWritable: false },
    didController: { value: input.didController ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.didDocument),
      getAccountMeta(accounts.didController),
    ],
    data: getResolveDidDocumentInstructionDataEncoder().encode({}),
    programAddress,
  } as ResolveDidDocumentInstruction<
    TProgramAddress,
    TAccountDidDocument,
    TAccountDidController
  >);
}

export type ParsedResolveDidDocumentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** DID document account */
    didDocument: TAccountMetas[0];
    /** Controller public key (used for PDA derivation) */
    didController: TAccountMetas[1];
  };
  data: ResolveDidDocumentInstructionData;
};

export function parseResolveDidDocumentInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedResolveDidDocumentInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 2) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      didDocument: getNextAccount(),
      didController: getNextAccount(),
    },
    data: getResolveDidDocumentInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
